import React from 'react';
import styled, { useTheme, css } from 'styled-components/macro';
import { SPACINGS } from '../../utils/style';
import { IconChevron, IconArrow, IconWrapper } from '../../icons';
import { Link } from 'react-router-dom';

type PADDING = 'none';
type BOX_SHADOW_SIZE = 'xs' | 'sm' | 'base' | 'lg' | 'xl';

type CardStyledProps = {
  isAccordionOpen: boolean;
  backgroundColor?: string;
  borderColor?: string;
  padding?: PADDING;
  boxShadow?: BOX_SHADOW_SIZE | 'none';
};

const CardStyled = styled.div<CardStyledProps>`
  width: 100%;
  background-color: ${(p) => p.backgroundColor};
  border: ${(p) => p.borderColor && `1px solid ${p.borderColor}`};
  border-radius: 10px;
  box-shadow: ${(p) =>
    p.boxShadow === 'none'
      ? 'none'
      : p.boxShadow
      ? p.theme.boxShadows[p.boxShadow]
      : p.theme.boxShadows.xs};
  transition: box-shadow 0.15s;

  & > .body {
    padding: ${(p) => (p.padding === 'none' ? '0' : SPACINGS[6])};
  }

  & > .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid
      ${(p) => p.borderColor ?? p.theme.colors.secondary90w};
    /* padding: ${(p) => (p.padding === 'none' ? '0' : SPACINGS[6])}; */
    padding: ${SPACINGS[6]};

    ${(p) =>
      !p.isAccordionOpen &&
      css`
        border-bottom: none;
      `}
  }

  & > .header > .title {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  & > .hidden {
    display: none;
  }
`;

type CardProps = {
  accordion?: boolean;
  accordionInitialState?: boolean;
  accordionState?: boolean;
  accordionOnChange?: () => void;
  title?: React.ReactNode;
  titleAction?: ((e: any) => void) | string;
  subtitle?: React.ReactNode;
  borderColor?: string;
  backgroundColor?: string;
  boxShadow?: BOX_SHADOW_SIZE | 'none';
  padding?: PADDING;
  children: React.ReactNode;
  testid?: string;
  style?: React.CSSProperties;
};

const Card = React.forwardRef<HTMLDivElement, CardProps>((props, ref) => {
  const theme = useTheme();
  const {
    accordion,
    accordionInitialState,
    accordionState,
    accordionOnChange,
    padding,
    borderColor,
    backgroundColor = theme.colors.white,
    boxShadow,
    title,
    subtitle,
    titleAction,
    children,
    testid,
    style,
  } = props;

  const [isAccordionOpen, setIsAccordionOpen] = React.useState(
    accordionInitialState ?? true
  );

  React.useEffect(() => {
    if (accordionState != null) setIsAccordionOpen(accordionState);
  }, [accordionState]);

  function handleAccordionClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (accordion) {
      if (accordionOnChange) {
        accordionOnChange();
      } else {
        setIsAccordionOpen((b) => !b);
      }
    }
  }

  const action =
    typeof titleAction === 'string' ? (
      <Link to={titleAction} data-testid={`${testid}-action`}>
        <IconWrapper 
            style={{ cursor: 'pointer'}}>
          <IconArrow stroke={theme.colors.primary20b} direction="right" />
        </IconWrapper>
      </Link>
    ) : typeof titleAction === 'function' ? (
      <IconWrapper
        style={{ cursor: 'pointer', userSelect: 'none'}}
        onClick={(e) => {
          e.stopPropagation();
          titleAction(e);
        }}
        data-testid={`${testid}-action`}
      >
        <IconArrow stroke={theme.colors.primary20b} direction="right" />
      </IconWrapper>
    ) : null;

  return (
    <CardStyled
      ref={ref}
      isAccordionOpen={isAccordionOpen}
      borderColor={borderColor}
      backgroundColor={backgroundColor}
      boxShadow={boxShadow}
      padding={padding}
      data-testid={testid}
      style={style}
    >
      {title && (
        <div className="header" onClick={handleAccordionClick}>
          <div className="title">
            {title}
            {subtitle}
          </div>
          <div>
            {action}
            {accordion && (
              <IconWrapper
                style={{ cursor: 'pointer', marginLeft: SPACINGS[2], 
                minWidth: SPACINGS[8],
                minHeight: SPACINGS[8] }}
                onClick={handleAccordionClick}
                role="button"
                aria-label={
                  isAccordionOpen ? 'Close card button' : 'Open card button'
                }
                backgroundColor={theme.colors.primary95w}
                hoverColor={theme.colors.primary90w}>
                <IconChevron
                  stroke={theme.colors.primary20b}
                  direction={isAccordionOpen ? 'top' : 'bottom'}
                />
              </IconWrapper>
            )}
          </div>
        </div>
      )}
      <div className={`body ${isAccordionOpen ? '' : 'hidden'}`} style={{height: style?.height}}>
        {children}
      </div>
    </CardStyled>
  );
});

export default React.memo(Card);
